import React, { useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";

import * as FaIcons from "react-icons/fa";

import AOS from "aos";
import "aos/dist/aos.css";
import {
  FaPlus,
  FaEdit,
  FaTrash,
  FaSave,
  FaTimes,
  FaCheckCircle,
  FaSearch,
  FaTools,
} from "react-icons/fa";

import "./Services.css";
import { BASE_URL } from "../../Config/config";
export default function Services() {
  /*
  |--------------------------------------------------------------------------
  | BASE URL
  |--------------------------------------------------------------------------
  */

  /*
  |--------------------------------------------------------------------------
  | STATES
  |--------------------------------------------------------------------------
  */

  const [services, setServices] = useState([]);

  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);

  const itemsPerPage = 20;

  const [saving, setSaving] = useState(false);

  const [showForm, setShowForm] = useState(false);

  const [editingService, setEditingService] = useState(null);

  const [iconSearch, setIconSearch] = useState("");

  const [form, setForm] = useState({
    icon: "FaTools",
    title: "",
    description: "",
    features: ["", "", "", ""],
    displayOrder: 1,
  });
  /*
  |--------------------------------------------------------------------------
  | GET ALL FONT AWESOME ICONS
  |--------------------------------------------------------------------------
  |
  | This automatically gets icons exported from react-icons/fa.
  |
  */

  const allIcons = useMemo(() => {
    return Object.entries(FaIcons)
      .filter(([name, Icon]) => {
        return (
          (name.startsWith("Fa") && typeof Icon === "object") ||
          (name.startsWith("Fa") && typeof Icon === "function")
        );
      })
      .map(([name, Icon]) => ({
        name,
        Icon,
      }))
      .sort((a, b) => a.name.localeCompare(b.name));
  }, []);

  const filteredIcons = useMemo(() => {
    const search = iconSearch.toLowerCase().trim();

    if (!search) {
      return allIcons;
    }

    return allIcons.filter((item) => item.name.toLowerCase().includes(search));
  }, [allIcons, iconSearch]);

  /*
  |--------------------------------------------------------------------------
  | GET SELECTED ICON
  |--------------------------------------------------------------------------
  */

  const getIcon = (iconName) => {
    const IconComponent = FaIcons[iconName];

    if (IconComponent) {
      return <IconComponent />;
    }

    return <FaTools />;
  };

  /*
  |--------------------------------------------------------------------------
  | FETCH SERVICES
  |--------------------------------------------------------------------------
  */

  const fetchServices = async () => {
    try {
      setLoading(true);

      const response = await fetch(`${BASE_URL}/services.php?action=get`);

      const result = await response.json();

      console.log("SERVICES API RESPONSE:", result);

      if (result.status) {
        setServices(result.data || []);
      } else {
        alert(result.message || "Failed to fetch services");
      }
    } catch (error) {
      console.error("Fetch services error:", error);

      alert("Unable to load services");
    } finally {
      setLoading(false);
    }
  };

  /*
  |--------------------------------------------------------------------------
  | INITIAL FETCH
  |--------------------------------------------------------------------------
  */

  useEffect(() => {
    fetchServices();
  }, []);
  // =====================================================
  // PAGINATION
  // =====================================================

  const totalPages = Math.ceil(services.length / itemsPerPage);

  const paginatedServices = useMemo(() => {
    const startIndex = (currentPage - 1) * itemsPerPage;

    return services.slice(startIndex, startIndex + itemsPerPage);
  }, [services, currentPage]);

  // Reset to first page when services change
  useEffect(() => {
    if (totalPages > 0 && currentPage > totalPages) {
      setCurrentPage(totalPages);
    }

    if (services.length === 0) {
      setCurrentPage(1);
    }
  }, [services, currentPage, totalPages]);
  /*
  |--------------------------------------------------------------------------
  | OPEN ADD FORM
  |--------------------------------------------------------------------------
  */

  const openAddForm = () => {
    setEditingService(null);

    setIconSearch("");

    setForm({
      icon: "FaTools",
      title: "",
      description: "",
      features: ["", "", "", ""],
      displayOrder: services.length + 1,
    });

    setShowForm(true);
  };

  /*
  |--------------------------------------------------------------------------
  | OPEN EDIT FORM
  |--------------------------------------------------------------------------
  */
  useEffect(() => {
    AOS.init({
      duration: 700,
      easing: "ease-out-cubic",
      once: true,
      offset: 80,
    });

    AOS.refresh();
  }, []);
  const openEditForm = (service) => {
    setEditingService(service);

    setIconSearch("");

    setForm({
      icon: service.icon || "FaTools",
      title: service.title || "",
      description: service.description || "",
      features: [
        service.features?.[0] || "",
        service.features?.[1] || "",
        service.features?.[2] || "",
        service.features?.[3] || "",
      ],
      displayOrder: service.display_order || 1,
    });

    setShowForm(true);
  };

  /*
  |--------------------------------------------------------------------------
  | CLOSE FORM
  |--------------------------------------------------------------------------
  */

  const closeForm = () => {
    if (saving) return;

    setShowForm(false);

    setEditingService(null);

    setIconSearch("");
  };

  /*
  |--------------------------------------------------------------------------
  | UPDATE FEATURE
  |--------------------------------------------------------------------------
  */

  const updateFeature = (index, value) => {
    setForm((prev) => {
      const updatedFeatures = [...prev.features];

      updatedFeatures[index] = value;

      return {
        ...prev,
        features: updatedFeatures,
      };
    });
  };

  /*
  |--------------------------------------------------------------------------
  | SAVE SERVICE
  |--------------------------------------------------------------------------
  */

  const saveService = async () => {
    /*
    |--------------------------------------------------------------------------
    | VALIDATION
    |--------------------------------------------------------------------------
    */

    if (!form.title.trim()) {
      alert("Service title is required");
      return;
    }

    if (!form.description.trim()) {
      alert("Service description is required");
      return;
    }

    const cleanFeatures = form.features
      .map((feature) => feature.trim())
      .filter((feature) => feature !== "");

    if (cleanFeatures.length === 0) {
      alert("Please add at least one feature");
      return;
    }

    /*
    |--------------------------------------------------------------------------
    | PAYLOAD
    |--------------------------------------------------------------------------
    */

    const payload = {
      icon: form.icon,
      title: form.title.trim(),
      description: form.description.trim(),
      features: cleanFeatures,
      display_order: Number(form.displayOrder) || 1,
    };
    try {
      setSaving(true);

      /*
      |--------------------------------------------------------------------------
      | UPDATE
      |--------------------------------------------------------------------------
      */

      if (editingService) {
        const response = await fetch(`${BASE_URL}/services.php?action=update`, {
          method: "POST",

          headers: {
            "Content-Type": "application/json",
          },

          body: JSON.stringify({
            ...payload,
            id: editingService.id,
          }),
        });

        const result = await response.json();

        console.log("UPDATE SERVICE:", result);

        if (!result.status) {
          alert(result.message || "Failed to update service");
          return;
        }

        alert("Service updated successfully");

        await fetchServices();

        closeForm();

        return;
      }

      /*
      |--------------------------------------------------------------------------
      | ADD
      |--------------------------------------------------------------------------
      */

      const response = await fetch(`${BASE_URL}/services.php?action=add`, {
        method: "POST",

        headers: {
          "Content-Type": "application/json",
        },

        body: JSON.stringify(payload),
      });

      const result = await response.json();

      console.log("ADD SERVICE:", result);

      if (!result.status) {
        alert(result.message || "Failed to add service");
        return;
      }

      alert("Service added successfully");

      await fetchServices();

      closeForm();
    } catch (error) {
      console.error("Save service error:", error);

      alert("Something went wrong while saving the service");
    } finally {
      setSaving(false);
    }
  };

  /*
  |--------------------------------------------------------------------------
  | DELETE SERVICE
  |--------------------------------------------------------------------------
  */

  const deleteService = async (id) => {
    const confirmed = window.confirm(
      "Are you sure you want to delete this service?",
    );

    if (!confirmed) {
      return;
    }

    try {
      const response = await fetch(
        `${BASE_URL}/services.php?action=delete&id=${id}`,
        {
          method: "DELETE",
        },
      );

      const result = await response.json();

      console.log("DELETE SERVICE:", result);

      if (!result.status) {
        alert(result.message || "Failed to delete service");
        return;
      }

      alert("Service deleted successfully");

      await fetchServices();
    } catch (error) {
      console.error("Delete service error:", error);

      alert("Unable to delete service");
    }
  };

  /*
  |--------------------------------------------------------------------------
  | LOADING
  |--------------------------------------------------------------------------
  */

  if (loading) {
    return (
      <div className="admin-services-page">
        <div className="admin-empty-services">
          <FaTools />

          <h3>Loading Services...</h3>

          <p>Please wait.</p>
        </div>
      </div>
    );
  }

  /*
  |--------------------------------------------------------------------------
  | UI
  |--------------------------------------------------------------------------
  */

  return (
    <div className="admin-services-page" data-aos="fade-up">
      {/* =====================================================
          HEADER
      ===================================================== */}

      <div
        className="admin-page-header"
        data-aos="fade-down"
        data-aos-duration="700"
      >
        <div>
          <span className="admin-page-label">SERVICE MANAGEMENT</span>

          <h1>Services</h1>

          <p>Manage the services displayed on the Star Computers website.</p>
        </div>

        <button className="admin-primary-btn" onClick={openAddForm}>
          <FaPlus />
          Add Service
        </button>
      </div>

      {/* =====================================================
          SERVICE LIST
      ===================================================== */}
      <div className="admin-services-list">
        {paginatedServices.map((service, index) => (
          <div
            className="admin-service-card"
            key={service.id}
            data-aos="fade-up"
            data-aos-delay={index * 100}
          >
            {/* TOP */}

            <div className="admin-service-top">
              <div className="admin-service-left">
                <div className="admin-service-icon">
                  {getIcon(service.icon)}
                </div>

                <div className="admin-service-heading">
                  {/* <span>
                    SERVICE{" "}
                    {String(
                      (currentPage - 1) * itemsPerPage + index + 1,
                    ).padStart(2, "0")}
                  </span> */}
                  <span>
                    SERVICE{" "}
                    {String(
                      paginatedServices.length -
                        ((currentPage - 1) * itemsPerPage + index),
                    ).padStart(2, "0")}
                  </span>
                  <h2>{service.title}</h2>
                </div>
              </div>

              <div className="admin-service-actions">
                <button onClick={() => openEditForm(service)} title="Edit">
                  <FaEdit />
                </button>

                <button
                  className="danger"
                  onClick={() => deleteService(service.id)}
                  title="Delete"
                >
                  <FaTrash />
                </button>
              </div>
            </div>

            {/* DESCRIPTION */}

            <p className="admin-service-description">{service.description}</p>

            {/* FEATURES */}

            <div className="admin-service-features">
              <div className="admin-feature-heading">
                <span>FEATURES</span>

                <strong>{service.features?.length || 0} Features</strong>
              </div>

              <div className="admin-feature-grid">
                {service.features?.map((feature, featureIndex) => (
                  <div className="admin-feature-item" key={featureIndex}>
                    <FaCheckCircle />

                    <span>{feature}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        ))}
      </div>
      {/* =====================================================
    PAGINATION
===================================================== */}

      {totalPages > 1 && (
        <div className="admin-services-pagination">
          {/* PREVIOUS */}
          <button
            type="button"
            disabled={currentPage === 1}
            onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 1))}
            className="admin-services-pagination-btn"
            title="Previous"
          >
            <FaIcons.FaChevronLeft />
          </button>

          {/* PAGE NUMBERS */}
          {Array.from({ length: totalPages }, (_, index) => index + 1)
            .filter((page) => {
              if (totalPages <= 5) {
                return true;
              }

              return (
                page === 1 ||
                page === totalPages ||
                Math.abs(page - currentPage) <= 1
              );
            })
            .map((page, index, pages) => {
              const previousPage = pages[index - 1];

              return (
                <React.Fragment key={page}>
                  {/* DOTS */}
                  {previousPage && page - previousPage > 1 && (
                    <span className="admin-services-pagination-dots">...</span>
                  )}

                  {/* PAGE */}
                  <button
                    type="button"
                    className={`admin-services-pagination-btn ${
                      currentPage === page ? "active" : ""
                    }`}
                    onClick={() => setCurrentPage(page)}
                  >
                    {page}
                  </button>
                </React.Fragment>
              );
            })}

          {/* NEXT */}
          <button
            type="button"
            disabled={currentPage === totalPages}
            onClick={() =>
              setCurrentPage((prev) => Math.min(prev + 1, totalPages))
            }
            className="admin-services-pagination-btn"
            title="Next"
          >
            <FaIcons.FaChevronRight />
          </button>
        </div>
      )}

      {/* =====================================================
          EMPTY
      ===================================================== */}

      {services.length === 0 && (
        <div className="admin-empty-services">
          <FaTools />

          <h3>No Services Found</h3>

          <p>Add your first service to display it on the website.</p>

          <button className="admin-primary-btn" onClick={openAddForm}>
            <FaPlus />
            Add Service
          </button>
        </div>
      )}

      {/* =====================================================
          MODAL
      ===================================================== */}

      {/* =====================================================
    MODAL
===================================================== */}

      {showForm &&
        createPortal(
          <div className="services-modal-overlay">
            <div
              className="services-modal"
              onClick={(e) => e.stopPropagation()}
            >
              {/* =================================================
            MODAL HEADER
        ================================================= */}

              <div className="admin-modal-header">
                <div>
                  <span>{editingService ? "EDIT SERVICE" : "NEW SERVICE"}</span>

                  <h2>{editingService ? "Edit Service" : "Add Service"}</h2>
                </div>

                <button
                  type="button"
                  onClick={closeForm}
                  className="services-modal-close"
                  disabled={saving}
                >
                  <FaTimes />
                </button>
              </div>

              {/* =================================================
            FORM
        ================================================= */}

              <div className="services-modal-form">
                {/* =================================================
              ICON
          ================================================= */}

                <div className="admin-form-group">
                  <div className="admin-features-form-title">
                    <label>Service Icon</label>

                    <span>Choose an icon</span>
                  </div>

                  {/* SEARCH */}

                  <div className="services-icon-search">
                    <FaSearch />

                    <input
                      type="text"
                      value={iconSearch}
                      onChange={(e) => setIconSearch(e.target.value)}
                      placeholder="Search icon..."
                    />
                  </div>

                  {/* SELECTED ICON */}

                  <div className="services-selected-icon">
                    <div className="services-selected-icon-preview">
                      {getIcon(form.icon)}
                    </div>

                    <div>
                      <strong>Selected Icon</strong>

                      <div>{form.icon}</div>
                    </div>
                  </div>

                  {/* ICON GRID */}

                  <div className="admin-icon-options">
                    {filteredIcons.map(({ name, Icon }) => (
                      <button
                        type="button"
                        key={name}
                        title={name}
                        className={form.icon === name ? "selected" : ""}
                        onClick={() =>
                          setForm((prev) => ({
                            ...prev,
                            icon: name,
                          }))
                        }
                      >
                        <span className="icon-option-icon">
                          <Icon />
                        </span>

                        <span>{name.replace(/^Fa/, "")}</span>
                      </button>
                    ))}
                  </div>
                </div>

                {/* =================================================
              TITLE
          ================================================= */}

                <div className="admin-form-group">
                  <label>Service Title</label>

                  <input
                    type="text"
                    value={form.title}
                    onChange={(e) =>
                      setForm((prev) => ({
                        ...prev,
                        title: e.target.value,
                      }))
                    }
                    placeholder="Tally Prime Solutions"
                  />
                </div>

                {/* =================================================
              DESCRIPTION
          ================================================= */}

                <div className="admin-form-group">
                  <label>Description</label>

                  <textarea
                    rows="4"
                    value={form.description}
                    onChange={(e) =>
                      setForm((prev) => ({
                        ...prev,
                        description: e.target.value,
                      }))
                    }
                    placeholder="Enter service description..."
                  />
                </div>

                {/* =================================================
              FEATURES
          ================================================= */}

                <div className="admin-form-group">
                  <div className="admin-features-form-title">
                    <label>Service Features</label>

                    <span>Add up to 4 features</span>
                  </div>

                  <div className="admin-feature-inputs">
                    {form.features.map((feature, index) => (
                      <div className="admin-feature-input" key={index}>
                        <div className="feature-input-number">{index + 1}</div>

                        <input
                          type="text"
                          value={feature}
                          onChange={(e) => updateFeature(index, e.target.value)}
                          placeholder={`Feature ${index + 1}`}
                        />
                      </div>
                    ))}
                  </div>
                </div>

                {/* =================================================
              DISPLAY ORDER
          ================================================= */}

                <div className="admin-form-group">
                  <label>Display Order</label>

                  <input
                    type="number"
                    min="1"
                    value={form.displayOrder}
                    onChange={(e) =>
                      setForm((prev) => ({
                        ...prev,
                        displayOrder: Number(e.target.value),
                      }))
                    }
                    placeholder="1"
                  />

                  <small>Lower numbers will appear first.</small>
                </div>

                {/* =================================================
              BUTTONS
          ================================================= */}

                <div className="services-modal-actions">
                  <button
                    type="button"
                    className="services-modal-cancel"
                    onClick={closeForm}
                    disabled={saving}
                  >
                    <FaTimes />
                    Cancel
                  </button>

                  <button
                    type="button"
                    className="services-modal-save"
                    onClick={saveService}
                    disabled={saving}
                  >
                    <FaSave />

                    {saving
                      ? "Saving..."
                      : editingService
                        ? "Update Service"
                        : "Save Service"}
                  </button>
                </div>
              </div>
            </div>
          </div>,

          document.body,
        )}
    </div>
  );
}
